import { Log } from "@fanfanlo";

const fileLog = new Log(true, 'ProjectCategory');
fileLog.pause = true;

/**
 * Project 类别枚举
 *
 * QuickAction - 快捷操作：只有一个 action，无 trigger
 * ScriptRunner - 脚本运行器：单 script action，纯代码
 * Workflow - 工作流：完整的 trigger + action
 */
export enum ProjectCategory {
  /** 快捷操作 - 只有一个 action，无 trigger */
  QuickAction = 'quick-action',

  /** 脚本运行器 - 单 script action，纯代码 */
  ScriptRunner = 'script-runner',

  /** 工作流 - 完整的 trigger + action */
  Workflow = 'workflow'
}

/**
 * Project 类别的 UI 配置
 */
export interface ProjectCategoryUIConfig {
  /** 是否显示 trigger 区域 */
  showTrigger: boolean;

  /** 是否显示 action 列表选择 */
  showActionList: boolean;

  /** 是否允许添加 triggerActionGroup */
  allowAddGroup: boolean;

  /** 是否强制使用 script action */
  forceScriptAction: boolean;
}

/**
 * Project 类别的完整配置
 */
export interface ProjectCategoryConfig {
  /** 类别 ID */
  category: ProjectCategory;

  /** 显示名称（i18n key） */
  labelKey: string;

  /** 描述（i18n key） */
  descriptionKey: string;

  /** 图标名称 (MUI icon) */
  icon: string;

  /** UI 控制配置 */
  uiConfig: ProjectCategoryUIConfig;
}

/**
 * 各类别的配置
 */
export const PROJECT_CATEGORY_CONFIGS: Record<ProjectCategory, ProjectCategoryConfig> = {
  [ProjectCategory.QuickAction]: {
    category: ProjectCategory.QuickAction,
    labelKey: 'ProjectCategory.QuickAction.label',
    descriptionKey: 'ProjectCategory.QuickAction.description',
    icon: 'flash_on',
    uiConfig: {
      showTrigger: false,
      showActionList: true,
      allowAddGroup: false,
      forceScriptAction: false
    }
  },

  [ProjectCategory.ScriptRunner]: {
    category: ProjectCategory.ScriptRunner,
    labelKey: 'ProjectCategory.ScriptRunner.label',
    descriptionKey: 'ProjectCategory.ScriptRunner.description',
    icon: 'code',
    uiConfig: {
      showTrigger: false,
      showActionList: false,
      allowAddGroup: false,
      forceScriptAction: true
    }
  },

  [ProjectCategory.Workflow]: {
    category: ProjectCategory.Workflow,
    labelKey: 'ProjectCategory.Workflow.label',
    descriptionKey: 'ProjectCategory.Workflow.description',
    icon: 'account_tree',
    uiConfig: {
      showTrigger: true,
      showActionList: true,
      allowAddGroup: true,
      forceScriptAction: false
    }
  }
};

/**
 * 获取 ProjectCategory 配置
 * @param category 项目类别，如果为空则默认返回 Workflow 配置
 */
export function getProjectCategoryConfig(category?: ProjectCategory): ProjectCategoryConfig {
  fileLog.log(`[getProjectCategoryConfig] 输入 category=${category}`);

  let resolvedCategory: ProjectCategory;
  if (category) {
    resolvedCategory = category;
    fileLog.log(`[getProjectCategoryConfig] 使用传入的 category=${resolvedCategory}`);
  } else {
    resolvedCategory = ProjectCategory.Workflow;
    fileLog.log(`[getProjectCategoryConfig] category 为空，使用默认值 Workflow`);
  }

  const config = PROJECT_CATEGORY_CONFIGS[resolvedCategory];
  fileLog.log(`[getProjectCategoryConfig] 返回配置:`, JSON.stringify(config));

  return config;
}
